import { FC, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from '@/components/ui/button';
import AnalyticsIcon from '@/components/icons/AnalyticsIcon';
import ApartmentIcon from '@/components/icons/ApartmentIcon';
import OrdersEmptyIcon from '@/components/icons/OrdersEmptyIcon';
import SquareLockIcon from '@/components/icons/SquareLockIcon';
import ClockEmptyIcon from '@/components/icons/ClockEmptyIcon';
import CalendarIcon from '@/components/icons/CalendarIcon';
import AgreementIcon from '@/components/icons/AgreementIcon';
import UserIcon from '@/components/icons/UserIcon';
import OwnerDetailsListCalendar from '@/features/owners/components/OwnersDetails/OwnerDetailsListCalendar';
import OwnerDetailsListApartments from '@/features/owners/components/OwnersDetails/OwnerDetailsListApartments';
import OwnerDetailsListFinances from '@/features/owners/components/OwnersDetails/OwnerDetailsListFinances';
import OrdersInfo from '@/features/orders/components/OrdersInfo/OrdersInfo';
import TrusteesList from '@/features/trustees/components/TrusteesList';
import AgreementsList from '@/features/trustees/components/agreements/AgreementsList';
import PermissionsList from '@/features/settings/components/permissions/PermissionsList';
import AccountLogs from '@/features/account/components/AccountLogs';
import { t } from 'i18next';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import SendIcon from '@/components/icons/SendIcon';
import AccountInvitationLogs from '@/features/account/components/AccountInvitationLogs';
import { OwnerDetailsResponseDto } from '@/features/owners/types/owners-dto';
import { useMultiCurrencyStorage } from '@/features/hotels/hooks/useHotelMultiCurrencyStorage';

enum OwnersDetailsListButtons {
	Apartments,
	Calendar,
	Finances,
	Orders,
	Agreements,
	Trustees,
	Permissions,
	ActivityLogs,
	InvitationLogs,
}

type Props = {
	ownerDetailsData: OwnerDetailsResponseDto;
};

const OwnerDetailsList: FC<Props> = ({ ownerDetailsData }) => {
	const [activeDetail, setActiveDetail] = useState(
		OwnersDetailsListButtons.Apartments,
	);

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { ordersEnabled } = useMultiCurrencyStorage();

	const clickHandler = (index: number) => {
		setActiveDetail(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const ownerDetailsButtonsList = [
		{
			type: OwnersDetailsListButtons.Apartments,
			name: t('main.owners.components.details.list.apartments.title'),
			icon: ApartmentIcon,
			enabled: () => true,
			component: (
				<OwnerDetailsListApartments
					id={ownerDetailsData.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: OwnersDetailsListButtons.Calendar,
			name: t('main.owners.components.details.list.calendar.title'),
			icon: CalendarIcon,
			enabled: () => true,
			component: (
				<OwnerDetailsListCalendar
					id={ownerDetailsData.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: OwnersDetailsListButtons.Finances,
			name: t('main.owners.components.details.list.finances.title'),
			icon: AnalyticsIcon,
			enabled: () => true,
			component: (
				<OwnerDetailsListFinances
					ownerId={ownerDetailsData.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: OwnersDetailsListButtons.Orders,
			name: t('main.owners.components.details.list.orders.title'),
			icon: OrdersEmptyIcon,
			enabled: () => ordersEnabled,
			component: <OrdersInfo isMobile={isMobile} />,
		},
		{
			type: OwnersDetailsListButtons.Agreements,
			name: t('main.owners.components.details.list.agreements.title'),
			icon: AgreementIcon,
			enabled: () => false,
			component: <AgreementsList isMobile={isMobile} />,
		},
		{
			type: OwnersDetailsListButtons.Trustees,
			name: t('main.owners.components.details.list.trustees.title'),
			icon: UserIcon,
			enabled: () => false,
			component: <TrusteesList isMobile={isMobile} />,
		},
		{
			type: OwnersDetailsListButtons.Permissions,
			name: t('main.owners.components.details.list.permissions.title'),
			icon: SquareLockIcon,
			enabled: () => true,
			component: (
				<PermissionsList
					ownerId={ownerDetailsData.id}
					isMobile={isMobile}
				/>
			),
		},
		{
			type: OwnersDetailsListButtons.ActivityLogs,
			name: t('main.owners.components.details.list.activityLogs.title'),
			icon: ClockEmptyIcon,
			enabled: () => false,
			component: <AccountLogs isMobile={isMobile} />,
		},
		{
			type: OwnersDetailsListButtons.InvitationLogs,
			name: t('main.owners.components.details.list.invitationLogs.title'),
			icon: SendIcon,
			enabled: () => true,
			component: (
				<AccountInvitationLogs
					id={ownerDetailsData.id}
					invitedAt={ownerDetailsData.invitedAt}
					isMobile={isMobile}
				/>
			),
		},
	];

	const visibleDetails = ownerDetailsButtonsList.filter((element) => {
		return element.enabled();
	});

	return (
		<div className='flex-1 flex flex-col gap-6'>
			<div
				className={cn(
					'-mx-6 px-6 overflow-hidden',
					isMobile && '-mx-4 px-4',
				)}
				ref={emblaRef}>
				<div className='flex gap-2 touch-pan-y'>
					{visibleDetails.map(({ type, name, icon: Icon }) => (
						<Button
							key={type}
							variant={
								activeDetail === type ? 'default' : 'outline'
							}
							className={cn(
								`h-9 px-3.5 py-2 hover:shadow-none ${
									activeDetail === type
										? ''
										: 'hover:bg-primary-color-50'
								} `,
							)}
							onClick={() => clickHandler(type)}>
							<Icon
								className={`${
									activeDetail === type
										? 'stroke-popover-10'
										: 'stroke-popover-1000'
								}`}
							/>
							{name}
						</Button>
					))}
				</div>
			</div>
			<div className='flex-1'>
				{
					visibleDetails.find(
						(element) => element.type === activeDetail,
					)?.component
				}
			</div>
		</div>
	);
};

export default OwnerDetailsList;
